import { useState, useEffect } from 'react';
import { Package, Plus, Trash2, ArrowUpRight, ArrowDownLeft } from 'lucide-react';
import Dropdown from '../receptura/Dropdown';
import { ACCENT as SECTION_COLOR, OK, WARN, BAD } from '../../lib/uiTokens';


const fmt = (v) => (v == null ? '—' : new Intl.NumberFormat('cs-CZ', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
}).format(v));

/**
 * Vratné (zálohované) obaly na dokladu — kegy, přepravky, sklo, láhve plynu.
 *
 * Záloha na obal NENÍ náklad ani předmět DPH (je to pohledávka vůči dodavateli),
 * proto se eviduje mimo nákladové položky a nezapočítává do food costu.
 *  - výdej   = koupili jsme obal (záloha +)
 *  - vrácení = odpočet obalů (záloha −)
 */
export default function ObalyPanel({ dokladId, pohyby = [], cardStyle, isDark, isAdmin, dokladZamceny, token, onChange }) {
    const [ciselnik, setCiselnik] = useState([]);
    const [pridavam, setPridavam] = useState(false);
    const [novy, setNovy] = useState({ obal_id: '', smer: 'vydej', mnozstvi: '1' });

    useEffect(() => {
        fetch('/api/finance/vratne-obaly/ciselnik')
            .then(r => (r.ok ? r.json() : []))
            .then(d => setCiselnik(Array.isArray(d) ? d : []))
            .catch(() => {});
    }, []);

    const editable = isAdmin && !dokladZamceny;

    // Čistý dopad na zálohy: výdej +, vrácení −
    const netto = pohyby.reduce(
        (s, p) => s + (p.smer === 'vraceni' ? -1 : 1) * parseFloat(p.castka || 0), 0,
    );

    const ulozit = async () => {
        if (!novy.obal_id) { alert('Vyber typ obalu.'); return; }
        const mn = parseFloat(novy.mnozstvi);
        if (!mn || mn <= 0) { alert('Zadej množství větší než 0.'); return; }

        const r = await fetch(`/api/finance/doklady/${dokladId}/obaly`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': token },
            body: JSON.stringify({ obal_id: Number(novy.obal_id), smer: novy.smer, mnozstvi: mn }),
        });
        if (r.ok) {
            setPridavam(false);
            setNovy({ obal_id: '', smer: 'vydej', mnozstvi: '1' });
            onChange?.();
        } else {
            const e = await r.json().catch(() => ({}));
            alert(e.message || 'Přidání obalu selhalo');
        }
    };

    const smazat = async (pohyb) => {
        if (!confirm(`Smazat pohyb "${pohyb.obal?.nazev || pohyb.nazev_z_dokladu}"?`)) return;
        const r = await fetch(`/api/finance/doklady/${dokladId}/obaly/${pohyb.id}`, {
            method: 'DELETE',
            headers: { 'X-CSRF-TOKEN': token },
        });
        if (r.ok) onChange?.();
        else alert('Smazání selhalo');
    };

    // Panel skryjeme, když nejsou pohyby a nejde přidávat (ať nezavazí u běžných dokladů)
    if (pohyby.length === 0 && !editable) return null;

    return (
        <div className="rounded-xl p-4" style={cardStyle}>
            <div className="flex items-center justify-between mb-3">
                <div>
                    <div className="text-xs font-medium flex items-center gap-1.5" style={{ color: 'var(--text-secondary)' }}>
                        <Package size={13} />
                        Vratné obaly ({pohyby.length})
                    </div>
                    <div className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>
                        Zálohy — nejsou náklad ani DPH
                        {pohyby.length > 0 && ` · netto ${netto > 0 ? '+' : ''}${fmt(netto)} Kč`}
                    </div>
                </div>
                {editable && !pridavam && (
                    <button
                        onClick={() => setPridavam(true)}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium"
                        style={{ background: 'rgba(59,130,246,0.1)', color: SECTION_COLOR }}
                    >
                        <Plus size={12} /> Přidat
                    </button>
                )}
            </div>

            {pohyby.length === 0 && !pridavam && (
                <div className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                    Na dokladu nejsou žádné vratné obaly.
                </div>
            )}

            <div className="space-y-1.5">
                {pohyby.map((p) => {
                    const vraceni = p.smer === 'vraceni';
                    const barva = vraceni ? OK : WARN;
                    return (
                        <div key={p.id} className="flex items-center gap-2 p-2 rounded-lg text-sm"
                            style={{ background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', border: '1px solid var(--border-default)' }}>
                            <span className="flex items-center justify-center rounded p-1 flex-shrink-0"
                                style={{ background: `${barva}1a`, color: barva }}
                                title={vraceni ? 'Vrácení (záloha zpět)' : 'Výdej (záloha zaplacena)'}>
                                {vraceni ? <ArrowDownLeft size={12} /> : <ArrowUpRight size={12} />}
                            </span>
                            <div className="flex-1 min-w-0">
                                <div className="truncate" style={{ color: 'var(--text-primary)' }}>
                                    {p.obal?.nazev || p.nazev_z_dokladu || 'Neznámý obal'}
                                    {!p.obal_id && (
                                        <span className="ml-1.5 text-[10px] px-1 rounded"
                                            style={{ background: `${WARN}26`, color: WARN }}
                                            title="Typ obalu není v číselníku — doplň ho ručně">
                                            mimo číselník
                                        </span>
                                    )}
                                </div>
                                <div className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>
                                    {fmt(p.mnozstvi)} ks × {fmt(p.zaloha_ks)} Kč
                                    {p.zdroj === 'manual' && ' · ručně'}
                                </div>
                            </div>
                            <span className="text-sm font-medium flex-shrink-0" style={{ color: barva }}>
                                {vraceni ? '−' : '+'}{fmt(p.castka)} Kč
                            </span>
                            {editable && (
                                <button onClick={() => smazat(p)} className="p-1 rounded hover:opacity-70 flex-shrink-0" title="Smazat pohyb">
                                    <Trash2 size={12} style={{ color: BAD }} />
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>

            {pridavam && (
                <div className="mt-2 p-2.5 rounded-lg space-y-2"
                    style={{ background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', border: '1px solid var(--border-default)' }}>
                    <Dropdown
                        value={novy.obal_id}
                        onChange={v => setNovy(s => ({ ...s, obal_id: v }))}
                        options={[
                            { value: '', label: '— typ obalu —' },
                            ...ciselnik.map(o => ({ value: o.id, label: `${o.nazev} (${fmt(o.zaloha_kc)} Kč)` })),
                        ]}
                        placeholder="— typ obalu —" isDark={isDark} accent={SECTION_COLOR} height={32}
                    />
                    <div className="grid grid-cols-2 gap-2">
                        <Dropdown
                            value={novy.smer}
                            onChange={v => setNovy(s => ({ ...s, smer: v }))}
                            options={[
                                { value: 'vydej', label: 'Výdej (koupeno)' },
                                { value: 'vraceni', label: 'Vrácení (odpočet)' },
                            ]}
                            isDark={isDark} accent={SECTION_COLOR} height={32}
                        />
                        <input
                            type="number" min="0.01" step="1" value={novy.mnozstvi}
                            onChange={e => setNovy(s => ({ ...s, mnozstvi: e.target.value }))}
                            placeholder="Množství (ks)"
                            className="px-2 py-1.5 rounded text-sm w-full"
                            style={{ background: isDark ? 'rgba(255,255,255,0.04)' : 'white', border: '1px solid var(--border-default)', color: 'var(--text-primary)' }}
                        />
                    </div>
                    <div className="flex gap-2">
                        <button onClick={ulozit}
                            className="px-2.5 py-1 rounded text-xs font-medium text-white" style={{ background: SECTION_COLOR }}>
                            Uložit
                        </button>
                        <button onClick={() => setPridavam(false)}
                            className="px-2.5 py-1 rounded text-xs" style={{ color: 'var(--text-secondary)' }}>
                            Zrušit
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
